import React, { useMemo } from 'react'
import ReactEChartsCore from '../../Component/Charts/EChartsCore.js'
import * as echarts from 'echarts/core'
import type { ComposeOption } from 'echarts/core'
import { LineChart, type LineSeriesOption } from 'echarts/charts'
import {
  DataZoomComponent,
  GridComponent,
  TooltipComponent,
  type DataZoomComponentOption,
  type GridComponentOption,
  type TooltipComponentOption
} from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import type { Datum } from './statsAggregations.js'

echarts.use([LineChart, GridComponent, TooltipComponent, DataZoomComponent, CanvasRenderer])

type EChartsOption = ComposeOption<LineSeriesOption | GridComponentOption | TooltipComponentOption | DataZoomComponentOption>

interface Props {
  data: Datum[]
  color?: string
  height?: number
  /** Show a draggable zoom/pan slider (for shifting around a time window). */
  zoom?: boolean
  /** Fix the y-axis max (so side-by-side period versions are visually comparable). */
  yMax?: number
}

/** Time-series line chart used for "by month" trends; `zoom` adds drag-to-pan + a range slider. */
export const StatsLineChart: React.FC<Props> = ({ data, color, height, zoom, yMax }) => {
  const option: EChartsOption = useMemo(
    () => ({
      backgroundColor: 'transparent',
      animation: false,
      tooltip: { trigger: 'axis', confine: true },
      grid: { top: 16, bottom: zoom ? 64 : 40, left: 48, right: 16, containLabel: true },
      xAxis: { type: 'category', data: data.map((d) => d.name), axisLabel: { rotate: 40, fontSize: 10 } },
      yAxis: { type: 'value', minInterval: 1, max: yMax },
      dataZoom: zoom ? [{ type: 'inside' }, { type: 'slider', height: 18, bottom: 8 }] : undefined,
      series: [
        {
          type: 'line',
          data: data.map((d) => d.value),
          smooth: false,
          showSymbol: data.length <= 60,
          itemStyle: { color: color ?? '#3b82f6' },
          areaStyle: { opacity: 0.08 }
        }
      ]
    }),
    [data, color, zoom, yMax]
  )

  return <ReactEChartsCore echarts={echarts} option={option} notMerge lazyUpdate style={{ width: '100%', height: height ?? 320 }} />
}
